//Authentication

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import { auth } from './firebase';
import { createUser, getUserByUid, checkUsernameExists, checkClassCodeExists } from './firestore';
import { User, SignupForm, LoginForm } from '../types';
import { DEFAULT_COLOR_KEY } from '../utils/colors';
import { getUserByUsername } from './firestore';


export const createInstructorAccount = async (data: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  username: string;
  animalId: string;
}): Promise<void> => {
  try {
    //Check username
    const usernameExists = await checkUsernameExists(data.username);
    if (usernameExists) {
      throw new Error('Username already exists');
    }

    //Create Firebase auth user
    const { user: firebaseUser } = await createUserWithEmailAndPassword(auth, data.email, data.password);
    
    //Instructor Doc
    const userData: Omit<User, 'uid'> = {
      username: data.username,
      firstName: data.firstName,
      lastName: data.lastName,
      initials: `${data.firstName[0]}${data.lastName[0]}`.toUpperCase(),
      email: data.email,
      animalId: data.animalId,
      userType: 'instructor' as const,
      isActive: false, //NOTE: Instructors start as inactive, NEED APPROVAL IN FS
      seeds: 0,
      water: 0,
      totalSeedsEarned: 0,
      totalSeedsPlanted: 0,
      totalWaterEarned: 0,
      totalWaterUsedOnOthers: 0,
      lastPosition: { x: 32, y: 18 },
      showIntroduction: false,
      preferredColor: DEFAULT_COLOR_KEY,
      completedExercises: [],
      lastExerciseDate: null,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await createUser(firebaseUser.uid, userData);
    
    console.log('Instructor account created:', data.username);
  } catch (error: any) {
    console.error('Error creating instructor account:', error);
    throw new Error(error.message || 'Failed to create instructor account');
  }
};


export const authService = {
  //Sign up new user
  signUp: async (formData: SignupForm): Promise<User> => {
    const email = formData.email || `${formData.username}-${formData.classCode}@temp.forestgame.local`;
    
    //Check if username already exists
    //ToDo: May change later to be unique to class
    const usernameExists = await checkUsernameExists(formData.username);
    if (usernameExists) {
      throw new Error('Username already exists');
    }

    //Verify class code exists
    const classCodeExists = await checkClassCodeExists(formData.classCode);
    if (!classCodeExists) {
      throw new Error('Invalid class code');
    }

    //FIREBASE:
    //Create Firebase auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, formData.password);
    const firebaseUser = userCredential.user;

    //Init user document
    const userData: Omit<User, 'uid'> = {
      username: formData.username,
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      initials: formData.initials.toUpperCase(),
      classCode: formData.classCode,
      animalId: formData.selectedAnimalId,
      userType: 'student',
      isActive: true,
      seeds: 0,
      water: 0,
      totalSeedsEarned: 0,
      totalSeedsPlanted: 0,
      totalWaterEarned: 0,
      totalWaterUsedOnOthers: 0,
      lastPosition: { x: 32, y: 18 },
      showIntroduction: true,
      preferredColor: DEFAULT_COLOR_KEY,
      completedExercises: [],
      lastExerciseDate: null,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const user = await createUser(firebaseUser.uid, userData);
    return user;
  },

  //Sign In
  signIn: async (formData: LoginForm): Promise<User> => {
    try {
      //Look up username to get type/email
      const userData = await getUserByUsername(formData.username);
      
      let email: string;
      
      if (userData && userData.userType === 'instructor' && userData.email) {
        email = userData.email;
      } else if (userData && userData.classCode) {
        email = `${formData.username}-${userData.classCode}@temp.forestgame.local`;
      } else {
        email = `${formData.username}@temp.forestgame.local`;
      }
      
      //Sign in
      const userCredential = await signInWithEmailAndPassword(auth, email, formData.password);
      const firebaseUser = userCredential.user;

      const user = await getUserByUid(firebaseUser.uid);
      if (!user) {
        throw new Error('User data not found');
      }

      return user;
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        throw new Error('Invalid username or password');
      }
      throw error;
    }
  },

  //Sign out
  signOut: async (): Promise<void> => {
    await signOut(auth);
  },

  onAuthStateChange: (callback: (user: User | null) => void): (() => void) => {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const user = await getUserByUid(firebaseUser.uid);
          callback(user);
        } catch (error) {
          console.error('Error fetching user data:', error);
          callback(null);
        }
      } else {
        callback(null);
      }
    });
  },

  getCurrentUser: (): Promise<User | null> => {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        unsubscribe();
        if (firebaseUser) {
          try {
            const user = await getUserByUid(firebaseUser.uid);
            resolve(user);
          } catch (error) {
            console.error('Error fetching current user:', error);
            resolve(null);
          }
        } else {
          resolve(null);
        }
      });
    });
  },
};
